import { useEffect, useState } from "react";
import styled from "styled-components";
import { lightShowDebug } from "./debug";

const REFRESH_MS = 250;
/** The newest Ably log lines shown, each cut to a couple of phone lines. */
const ABLY_LOG_LINES = 5;
const ABLY_LOG_WIDTH = 100;

const pad = (n: number, size = 2) => String(n).padStart(size, "0");

/** A server-clock time as HH:MM:SS.mmm, in the phone's time zone. */
const clockTime = (ms?: number) => {
  if (ms === undefined) return "—";
  const d = new Date(ms);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const signed = (ms: number) => `${ms >= 0 ? "+" : ""}${Math.round(ms)} ms`;

const ago = (localMs?: number) =>
  localMs === undefined
    ? "never"
    : `${Math.round((Date.now() - localMs) / 1000)}s ago`;

/** A short "iPhone · Safari 18"-style label from the user agent. */
const deviceLabel = () => {
  const ua = navigator.userAgent;
  const device = /iPhone/.test(ua)
    ? "iPhone"
    : /iPad/.test(ua)
      ? "iPad"
      : /Android/.test(ua)
        ? "Android"
        : /Macintosh/.test(ua)
          ? "Mac"
          : /Windows/.test(ua)
            ? "Windows"
            : "Other";
  const browser =
    ua.match(/(CriOS|FxiOS|EdgiOS|Edg|SamsungBrowser|Firefox|Chrome)\/(\d+)/) ??
    ua.match(/Version\/(\d+).*Safari/);
  const name = browser
    ? browser.length === 3
      ? `${browser[1]
          .replace("CriOS", "Chrome")
          .replace("FxiOS", "Firefox")
          .replace(/EdgiOS|Edg/, "Edge")} ${browser[2]}`
      : `Safari ${browser[1]}`
    : "unknown browser";
  return `${device} · ${name}`;
};

type Connection = { effectiveType?: string; rtt?: number; downlink?: number };

const networkLabel = () => {
  const connection = (navigator as Navigator & { connection?: Connection })
    .connection;
  if (!connection) return "unknown (browser doesn't say)";
  return [
    connection.effectiveType,
    connection.rtt !== undefined && `rtt ${connection.rtt} ms`,
    connection.downlink !== undefined && `${connection.downlink} Mb/s`,
  ]
    .filter(Boolean)
    .join(" · ");
};

/**
 * On-screen light show diagnostics (ENG-2291), for `?lightshowdebug`. Reads
 * what the light show records in `lightShowDebug` and shows, over
 * everything:
 * - server: the phone's estimate of the server clock now, and its offset;
 * - ably/change: the Ably connection, and when the last live change
 *   arrived vs when it should take effect (LATE means this phone switched
 *   late; changes made before the page opened aren't judged);
 * - applied: how far the scheduled switch drifted (throttled timers);
 * - frames: drawing rate and the slowest frame (a slow phone stutters);
 * - ABLY LOG: the Ably client's latest log lines (all of them go to the
 *   console too).
 */
export const DebugOverlay = ({ clockOffsetMs }: { clockOffsetMs: number }) => {
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  const { clock, ably, lastApply, frames } = lightShowDebug;
  const serverNow = Date.now() + clockOffsetMs;

  const frameTotal = frames.reduce((sum, ms) => sum + ms, 0);
  const fps = frameTotal > 0 ? (frames.length * 1000) / frameTotal : 0;
  const worstFrame = frames.length ? Math.max(...frames) : 0;

  const last = ably?.lastMessage;
  const madeBeforeOpen =
    last?.startedAtMs !== undefined &&
    last.startedAtMs < lightShowDebug.openedAt + clockOffsetMs;
  const lateBy =
    last?.startAtMs !== undefined
      ? last.receivedAtMs - last.startAtMs
      : undefined;

  // Kept to about 50 characters, to fit a phone.
  const lines = [
    "LIGHT SHOW DEBUG",
    `server  ${clockTime(serverNow)} · phone ${signed(-clockOffsetMs)}`,
    clock
      ? `clock   offset ${signed(clock.offsetMs)} · rtt ${Math.round(clock.roundTripMs)} ms · ${ago(clock.syncedAt)}${clock.failedSamples ? ` · ${clock.failedSamples} failed` : ""}`
      : "clock   not synced (no /time?)",
    `ably    ${ably?.state ?? "not started"} · ${ably?.channel ?? "—"} · ${ably?.messages ?? 0} msgs`,
    ably?.clientId && `        id ${ably.clientId}`,
    ably?.lastError && `        error ${ably.lastError}`,
    last ? `change  "${last.lookName ?? "off"}"` : "change  none yet",
    last &&
      `        got ${clockTime(last.receivedAtMs)}${
        last.startedAtMs !== undefined
          ? ` · ${signed(last.receivedAtMs - last.startedAtMs)} after tap`
          : ""
      }`,
    last &&
      (madeBeforeOpen
        ? "        made before this page opened"
        : lateBy !== undefined &&
          `        ${lateBy > 0 ? `LATE by ${Math.round(lateBy)} ms` : `on time, ${Math.round(-lateBy)} ms to spare`}`),
    lastApply
      ? `applied ${clockTime(lastApply.appliedMs)} · ${signed(lastApply.appliedMs - lastApply.scheduledMs)} vs plan`
      : "applied nothing yet",
    `frames  ${frames.length ? `${fps.toFixed(0)} fps · worst ${Math.round(worstFrame)} ms` : "not drawing"}`,
    `screen  ${lightShowDebug.fanState ?? "—"} · ${document.visibilityState} · lock ${lightShowDebug.wakeLock ?? "—"}`,
    `device  ${deviceLabel()}`,
    `network ${networkLabel()}`,
    ...(lightShowDebug.ablyLogs.length
      ? [
          "ABLY LOG",
          ...lightShowDebug.ablyLogs
            .slice(-ABLY_LOG_LINES)
            .map((line) =>
              line.length > ABLY_LOG_WIDTH
                ? `${line.slice(0, ABLY_LOG_WIDTH - 1)}…`
                : line,
            ),
        ]
      : []),
  ].filter(Boolean);

  return <Panel aria-hidden>{lines.join("\n")}</Panel>;
};

const Panel = styled.pre`
  position: fixed;
  top: max(8px, env(safe-area-inset-top));
  left: 8px;
  z-index: 2147483647;
  box-sizing: border-box;
  max-width: calc(100vw - 16px);
  margin: 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.78);
  color: #ffffff;
  font:
    11px/1.45 ui-monospace,
    Menlo,
    Consolas,
    monospace;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  pointer-events: none;
`;
